import { Box, Heading, Image, Link, SimpleGrid, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { sf } from './themeConfig/sisuForgeTokens';
import shieldUrl from './assets/sisuforge/sisu-forge-shield.png';

const CROHAM_URL = process.env.REACT_APP_CROHAM_URL ?? 'https://croham.sisuforge.com';

const PUBLIC = process.env.PUBLIC_URL ?? '';

type Product = {
  name: string;
  description: string;
  tag: string;
  /** In-app route (Sisu Forge hub) */
  to?: string;
  /** External product site */
  href?: string;
};

const products: Product[] = [
  {
    name: 'Crypto tools',
    description: 'Games, wallet, trackers, NFTs — opens the Croham City site for the full experience.',
    href: CROHAM_URL,
    tag: 'croham.sisuforge.com',
  },
  {
    name: 'Travel Buddy',
    description: 'Trips, receipts, routes, packing lists — local-first in your browser.',
    to: '/travel-buddy',
    tag: 'Hub app',
  },
  {
    name: 'Crypto tax (Finland)',
    description: 'FIFO disposal reporting, CSV import, Vero-oriented exports — runs in-browser.',
    to: '/crypto-tax-fi',
    tag: 'Hub app',
  },
];

export default function PortalHome() {
  return (
    <Box
      maxW="920px"
      mx="auto"
      px={{ base: 4, sm: 6, md: 8 }}
      py={{ base: 6, md: 10 }}
      pb={{ base: 10, md: 14 }}
      minH="100vh"
      display="flex"
      flexDirection="column"
    >
      <Box
        as="header"
        mb={{ base: 8, md: 10 }}
        textAlign="center"
        borderBottomWidth="1px"
        borderColor={sf.border}
        pb={{ base: 6, md: 8 }}
      >
        <Box
          display="flex"
          flexDirection={{ base: 'column', sm: 'row' }}
          alignItems="center"
          justifyContent="center"
          gap={{ base: 4, sm: 5 }}
        >
          <Image
            src={shieldUrl}
            alt=""
            w={{ base: '64px', sm: '72px' }}
            h={{ base: '64px', sm: '72px' }}
            objectFit="contain"
            flexShrink={0}
          />
          <Image
            src={`${PUBLIC}/sisu-forge-wordmark.svg`}
            alt="Sisu Forge"
            maxH={{ base: '36px', sm: '44px' }}
            w="auto"
            maxW="min(100%, 320px)"
            objectFit="contain"
          />
        </Box>
        <Text
          mt={5}
          fontSize="xs"
          fontWeight="semibold"
          letterSpacing="0.2em"
          textTransform="uppercase"
          color={sf.textMuted}
          fontFamily={sf.fontSans}
        >
          Apps & tools
        </Text>
        <Heading
          as="h1"
          mt={4}
          fontFamily={sf.fontDisplay}
          fontSize={{ base: '1.5rem', sm: '1.85rem', md: '2rem' }}
          fontWeight="700"
          lineHeight={1.2}
          color={sf.text}
          px={2}
        >
          Build with stubborn clarity.
        </Heading>
        <Text
          mt={3}
          mx="auto"
          maxW="lg"
          fontSize={{ base: 'sm', md: 'md' }}
          lineHeight={1.65}
          color={sf.textMuted}
          px={1}
        >
          Travel Buddy and the Finland crypto tax helper live here. Use <strong>Crypto tools</strong> for the Croham
          City site when you want the full game and wallet stack.
        </Text>
      </Box>

      <Box as="section" aria-label="Products" flex="1">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 3, md: 4 }}>
          {products.map((p) => {
            const common = {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              textDecoration: 'none',
              p: { base: 4, md: 5 },
              borderRadius: sf.radiusLg,
              borderWidth: '1px',
              borderColor: sf.border,
              bg: sf.surface,
              transition: `border-color ${sf.transitionFast}, box-shadow ${sf.transitionFast}, transform ${sf.transitionFast}`,
              _hover: {
                borderColor: 'rgba(58, 141, 255, 0.45)',
                boxShadow: `0 0 0 1px rgba(58, 141, 255, 0.12), 0 0 16px ${sf.accentMuted}`,
                transform: 'translateY(-1px)',
              },
              _focusVisible: {
                outline: 'none',
                boxShadow: `0 0 0 2px ${sf.bg}, 0 0 0 4px ${sf.accent}`,
              },
            } as const;

            const inner = (
              <>
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  letterSpacing="0.12em"
                  textTransform="uppercase"
                  color={sf.textSubtle}
                  mb={2}
                >
                  {p.tag}
                </Text>
                <Heading as="h2" size="md" mb={2} color={sf.text} fontFamily={sf.fontDisplay}>
                  {p.name}
                </Heading>
                <Text m={0} mb={4} fontSize="sm" lineHeight={1.55} color={sf.textMuted} flex="1">
                  {p.description}
                </Text>
                <Text fontSize="sm" fontWeight="600" color={sf.accent} letterSpacing="0.02em">
                  Open →
                </Text>
              </>
            );

            if (p.to) {
              return (
                <Link
                  key={p.to}
                  as={RouterLink}
                  to={p.to}
                  {...common}
                >
                  {inner}
                </Link>
              );
            }
            return (
              <Link
                key={p.href}
                as="a"
                href={p.href}
                rel="noopener noreferrer"
                {...common}
              >
                {inner}
              </Link>
            );
          })}
        </SimpleGrid>
      </Box>

      <Box
        as="footer"
        mt={{ base: 10, md: 12 }}
        pt={6}
        borderTopWidth="1px"
        borderColor={sf.border}
        fontSize="xs"
        lineHeight={1.6}
        color={sf.textSubtle}
        textAlign="center"
      >
     
        
      </Box>
    </Box>
  );
}
